/**
 * Zahlungsabgleich-Quote: Anteil verbuchter Zahlungen im Zeitraum.
 * < 80 % rot · 80–99 % gelb · 100 % grün (semantische Farben aus DESIGN.md).
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtEUR } from '@/lib/buchhaltungApi';
import { reconcileStats } from '@/lib/reconcileApi';
import { PERIODS, periodRange, quotePercent, quoteLevel } from '@/lib/reconcileLogic';

const LEVEL = {
  rot:   { color: '#DC2626', text: 'text-red-700', label: 'Viele Zahlungen offen' },
  gelb:  { color: '#D97706', text: 'text-amber-700', label: 'Fast alles verbucht' },
  gruen: { color: '#059669', text: 'text-emerald-700', label: 'Alles verbucht' },
};

function Ring({ percent, color }) {
  const r = 44;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 100 100" className="h-28 w-28 shrink-0 -rotate-90" aria-hidden="true">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#E2E8F0" strokeWidth="10" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(percent, 100) / 100)}
        className="transition-[stroke-dashoffset] duration-300 motion-reduce:transition-none" />
    </svg>
  );
}

export default function AbgleichsquoteCard({ className, compact = false }) {
  const [period, setPeriod] = useState('monat');
  const range = periodRange(period);
  const { data, error, isLoading } = useQuery({
    queryKey: ['fibu-reconcile-stats', range.from, range.to],
    queryFn: () => reconcileStats(range.from, range.to),
    retry: false,
    staleTime: 60_000,
  });
  // Ohne Migration/Berechtigung still ausblenden
  if (error) return null;

  const pct = quotePercent(data);
  const level = LEVEL[quoteLevel(pct)];

  return (
    <section aria-labelledby="abgleich-title" className={cn('rounded-xl border border-slate-200 bg-white p-4 md:p-5', className)}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Buchhaltung</p>
          <h2 id="abgleich-title" className="font-display text-lg font-bold text-slate-900">Zahlungsabgleich</h2>
        </div>
        <label className="sr-only" htmlFor="abgleich-period">Zeitraum</label>
        <select id="abgleich-period" value={period} onChange={(e) => setPeriod(e.target.value)}
          className="h-11 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400">
          {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="mt-4 h-28 animate-pulse rounded-lg bg-slate-100" />
      ) : (
        <div className="mt-3 flex items-center gap-4">
          <div className="relative">
            <Ring percent={pct} color={level.color} />
            <span className={cn('absolute inset-0 flex items-center justify-center font-display text-2xl font-bold tabular-nums', level.text)}>
              {pct.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %
            </span>
          </div>
          <div className="min-w-0 space-y-1 text-sm">
            <p className={cn('font-semibold', level.text)}>{level.label}</p>
            <p className="tabular-nums text-slate-700">{data?.done ?? 0} von {data?.total ?? 0} Zahlungen verbucht</p>
            {data?.open > 0 && <p className="tabular-nums text-slate-500">{data.open} offen · {fmtEUR(data.open_amount)}</p>}
            <p className="text-xs text-slate-500">{new Date(range.from).toLocaleDateString('de-DE')} – {new Date(range.to).toLocaleDateString('de-DE')}</p>
          </div>
        </div>
      )}

      {!compact && (data?.by_connection || []).length > 1 && (
        <ul className="mt-4 space-y-2">
          {data.by_connection.map((c) => {
            const p = quotePercent(c);
            return (
              <li key={c.connection_id} className="text-xs">
                <div className="flex justify-between text-slate-600"><span className="truncate">{c.name}</span><span className="tabular-nums">{c.done}/{c.total}</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-slate-100">
                  <div className="h-1.5 rounded-full" style={{ width: `${Math.min(p, 100)}%`, background: LEVEL[quoteLevel(p)].color }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {data?.open > 0 && (
        <Link to="/buchhaltung/bank" className="mt-4 inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-slate-900 hover:underline">
          Offene Zahlungen buchen <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      )}
    </section>
  );
}
